import { useState, useEffect, useRef } from 'react';
import { useFlag } from '@unleash/proxy-client-react';
import { authorize } from 'services/auth';
import {
  AuthorizeContextValue,
  withAuthorizeContext,
} from 'pages/Authorize/AuthorizeContext';
import LoaderButton from 'components/Button/LoaderButton';
import { SelectCompany } from '@finch-api/common/dist/internal/connect/authorize';
import { FeatureFlag } from 'constants/feature-flags';
import { useTrackConversionStep } from 'services/track-conversion';

import styles from './ChooseMultiAccounts.module.scss';
import { step } from '../steps';
import GenericError from '../Errors/GenericError';
import LoadingSpinner from '../LoadingSpinner';
import { getStatusCode } from '../SignIn/utils';
import { ProviderAppShell } from '../ProviderAppShell';
import PreviouslyConnectedAccounts from './PreviouslyConnectedAccounts';
import UnavailableEntities from './UnavailableEntities';
import CollapsibleSection from './CollapsibleSection';
import {
  EntityFilterInput,
  FILTER_THRESHOLD,
  buildEntityRows,
  filterEntityRows,
  sortAccountsByName,
} from '../EntitySelection';

// TODO temporary type until common api update is merged
// TODO: Remove `previouslySelected` once backwards-compatible code is cleaned up on the API server.
// See: https://linear.app/finch/issue/CONN-3628/api-server-remove-backwards-compatible-code-for-multi-parent-entities
type AccountWithSelection = SelectCompany & {
  previouslySelected?: boolean;
  isAvailable?: boolean;
  unavailableReason?: string;
};

enum UnavailableReason {
  LINKED_TO_OTHER_PARENT = 'LINKED_TO_OTHER_PARENT',
  ALREADY_LINKED_TO_CURRENT_PARENT = 'ALREADY_LINKED_TO_CURRENT_PARENT',
}

const getTooltipForUnavailableReason = (reason?: string): string => {
  switch (reason) {
    case UnavailableReason.LINKED_TO_OTHER_PARENT:
      return 'These entities are already associated with a different active connection.';
    case UnavailableReason.ALREADY_LINKED_TO_CURRENT_PARENT:
      return 'These entities are already associated with this connection and cannot be disconnected.';
    default:
      return 'These entities are not available to connect.';
  }
};

const DEFAULT_ERROR_MESSAGE =
  'Looks like there was an issue selecting accounts. Please try again.';

const ChooseMultiAccounts = ({
  clientId,
  connectionId,
  clientName,
  redirectUri,
  products,
  sessionKey,
  currentBrowserSessionKey,
  state,
  category,
  payrollProvider,
  providerImplementation,
  currentStep,
  accounts,
  handleAuthorizeResponse,
  sandbox,
  appType,
  setError,
  implementationHasAssistedBenefits,
  setCurrentStep,
  error,
}: AuthorizeContextValue) => {
  if (!payrollProvider) throw new Error('no payroll provider');
  if (!providerImplementation) throw new Error('no provider implementation');

  const disableEntitiesEnabled =
    useFlag(FeatureFlag.MultiAccountConnectDisableEntitiesScreen) ||
    process.env.NODE_ENV !== 'production';

  // Split accounts based on whether they were previously selected
  // TODO: Remove `previouslySelected` once backwards-compatible code is cleaned up on the API server.
  // See: https://linear.app/finch/issue/CONN-3628/api-server-remove-backwards-compatible-code-for-multi-parent-entities
  const accountsWithSelection = accounts as AccountWithSelection[];
  const previouslyConnectedAccounts = accountsWithSelection.filter(
    (account) =>
      account.previouslySelected ||
      account.unavailableReason === 'ALREADY_LINKED_TO_CURRENT_PARENT',
  );
  const unavailableAccounts = disableEntitiesEnabled
    ? accountsWithSelection.filter(
        (account) =>
          account.isAvailable === false &&
          account.unavailableReason !== 'ALREADY_LINKED_TO_CURRENT_PARENT',
      )
    : [];
  const newAccounts = accountsWithSelection.filter(
    (account) => !account.previouslySelected && account.isAvailable !== false,
  );

  const [loading, setLoading] = useState(false);
  const [selectedIndices, setSelectedIndices] = useState<number[]>([]);
  const [filter, setFilter] = useState('');

  const trackConversion = useTrackConversionStep();
  const mounted = useRef(false);

  useEffect(() => {
    if (!mounted.current) {
      trackConversion({ action: 'display' });
      mounted.current = true;
    }
  }, [trackConversion]);

  // Determine if this is a reauth scenario based on whether there are previously connected accounts
  const isReauth = previouslyConnectedAccounts.length > 0;

  // `selectedIndices` holds positions in `newAccounts`, so every row carries its
  // own position and the rendered order can be sorted or filtered freely.
  const orderedNewAccounts = buildEntityRows(newAccounts);
  const visibleNewAccounts = filterEntityRows(orderedNewAccounts, filter);
  const showFilter = newAccounts.length > FILTER_THRESHOLD;
  const isFiltering = filter.trim().length > 0;

  const allVisibleSelected =
    visibleNewAccounts.length > 0 &&
    visibleNewAccounts.every((row) => selectedIndices.includes(row.index));

  const toggleSelection = (index: number) => {
    setSelectedIndices((prev) => {
      if (prev.includes(index)) {
        return prev.filter((selected) => selected !== index);
      }

      return [...prev, index];
    });
  };

  // Scoped to the rows currently shown, so a filtered "Select All" never touches
  // entities the user cannot see.
  const toggleSelectAll = () => {
    const visibleIndices = visibleNewAccounts.map((row) => row.index);

    setSelectedIndices((prev) => {
      if (allVisibleSelected) {
        return prev.filter((selected) => !visibleIndices.includes(selected));
      }

      const next = [...prev];

      for (const index of visibleIndices) {
        if (!next.includes(index)) {
          next.push(index);
        }
      }

      return next;
    });
  };

  const onConfirmSelection = async () => {
    const selectedNewAccounts = selectedIndices.map((i) => newAccounts[i]);
    const allSelectedAccounts = [
      ...previouslyConnectedAccounts,
      ...selectedNewAccounts,
    ];

    if (allSelectedAccounts.length === 0) return;

    setError(null);
    setLoading(true);
    trackConversion({ action: 'submit' });

    try {
      const authorizeResponse = await authorize({
        clientId,
        connectionId,
        clientName,
        redirectUri,
        products,
        sessionKey,
        currentBrowserSessionKey,
        state,
        category,
        step: currentStep,
        payrollProviderId: payrollProvider.id,
        providerImplementationId: providerImplementation.id,
        chooseMultiAccounts: allSelectedAccounts, // using 'chosenMultiAccounts' instead of 'chosenAccount'
        sandbox,
        appType,
        implementationHasAssistedBenefits,
      });

      handleAuthorizeResponse(authorizeResponse);
      if (!('redirect' in authorizeResponse)) {
        setLoading(false);
      }
    } catch (err: any) {
      setLoading(false);

      const message =
        err.message === 'Internal Server Error'
          ? DEFAULT_ERROR_MESSAGE
          : err.message ?? DEFAULT_ERROR_MESSAGE;

      setError({
        message,
        formattedMessage: err?.response?.data?.formatted_message,
        hideToast: true,
        finchCode: err.response?.data?.finch_code,
        status: getStatusCode(err),
      });
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <GenericError
        title="Error"
        buttonLabel="Back to Sign In"
        onClick={() => {
          setError(null);
          setCurrentStep(step.SIGN_IN);
        }}
      >
        {error.message}
      </GenericError>
    );
  }

  const newAccountsList = visibleNewAccounts.map((row) => {
    const isSelected = selectedIndices.includes(row.index);

    return (
      <label
        key={row.index}
        className={[
          styles.accountContainer,
          isSelected && styles.selected,
        ].join(' ')}
      >
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => toggleSelection(row.index)}
          disabled={loading}
        />
        <span className={styles.companyName}>{row.account.companyName}</span>
      </label>
    );
  });

  return (
    <ProviderAppShell
      error={error}
      setError={setError}
      provider={payrollProvider}
      providerImplementation={providerImplementation}
      goBack={() => setCurrentStep(step.SIGN_IN)}
      headerText={
        isReauth
          ? 'Select additional entities to connect'
          : 'Select entities to connect'
      }
    >
      <div className={styles.contentContainer}>
        {/* Section 1: Available to connect - default open */}
        {newAccounts.length > 0 && (
          <CollapsibleSection
            title="Available to connect"
            defaultOpen={true}
            tooltipContent="These entities are available to add to this connection."
            forceExpanded={true}
          >
            <div className={styles.newAccountsSection}>
              <div className={styles.persistentTop}>
                {showFilter && (
                  <EntityFilterInput
                    value={filter}
                    onChange={setFilter}
                    disabled={loading}
                  />
                )}
                <div className={styles.selectAllContainer}>
                  <label className={styles.selectAllOption}>
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={toggleSelectAll}
                      disabled={loading || visibleNewAccounts.length === 0}
                    />
                    <span className={styles.selectAllText}>
                      {isFiltering
                        ? `Select All (${visibleNewAccounts.length})`
                        : 'Select All'}
                    </span>
                  </label>
                  {selectedIndices.length > 0 && (
                    <span className={styles.selectedCount}>
                      {selectedIndices.length} selected
                    </span>
                  )}
                </div>
              </div>
              <div className={styles.scrollableContent}>
                <div className={styles.accountsContainer}>
                  {newAccountsList}
                  {visibleNewAccounts.length === 0 && (
                    <p className={styles.emptyState}>
                      No entities match “{filter.trim()}”.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </CollapsibleSection>
        )}

        {/* Section 2: Connected entities - default closed */}
        {isReauth && (
          <CollapsibleSection
            title="Connected entities"
            defaultOpen={false}
            tooltipContent={getTooltipForUnavailableReason(
              UnavailableReason.ALREADY_LINKED_TO_CURRENT_PARENT,
            )}
            forceExpanded={true}
          >
            <div className={styles.previousAccountsSection}>
              <PreviouslyConnectedAccounts
                accounts={sortAccountsByName(previouslyConnectedAccounts)}
              />
            </div>
          </CollapsibleSection>
        )}

        {/* Section 3: Unavailable to connect - default closed */}
        {disableEntitiesEnabled && unavailableAccounts.length > 0 && (
          <CollapsibleSection
            title="Unavailable to connect"
            defaultOpen={false}
            tooltipContent={getTooltipForUnavailableReason(
              unavailableAccounts[0]?.unavailableReason,
            )}
            forceExpanded={true}
          >
            <div className={styles.previousAccountsSection}>
              <UnavailableEntities
                accounts={sortAccountsByName(unavailableAccounts)}
              />
            </div>
          </CollapsibleSection>
        )}
        <div className={styles.stickyButtonContainer}>
          <LoaderButton
            text={loading ? 'Connecting...' : 'Connect'}
            onClick={onConfirmSelection}
            disabled={
              loading ||
              (selectedIndices.length === 0 &&
                previouslyConnectedAccounts.length === 0)
            }
            isLoading={loading}
          />
        </div>
      </div>
    </ProviderAppShell>
  );
};

export default withAuthorizeContext(ChooseMultiAccounts);
